<template>
  <div class="mahai-auth">
    <!-- Background courthouse photo -->
    <img
      :src="require('@/assets/images/courthouse.jpg')"
      alt=""
      class="bg-courthouse animate-fade-in"
    />
    <!-- Dark overlay -->
    <div class="bg-overlay animate-fade-in-overlay"></div>

    <!-- Stats chips -->
    <div v-if="stats" class="stats-chips animate-fade-in">
      <div class="stat-chip">
        <span class="stat-dot green"></span>
        <div class="stat-text">
          <span class="stat-num">{{ stats.yasalar.toLocaleString('tr-TR') }}</span>
          <span class="stat-label">Yasa &amp; Tüzük</span>
        </div>
      </div>
      <div class="stat-chip">
        <span class="stat-dot blue"></span>
        <div class="stat-text">
          <span class="stat-num">{{ stats.kararlar.toLocaleString('tr-TR') }}</span>
          <span class="stat-label">YM İçtihatları</span>
        </div>
      </div>
    </div>

    <!-- Login card -->
    <div class="mahai-auth-card animate-slide-up">
      <div class="mahai-auth-logo">
        <img :src="require('@/assets/images/mahai-logo.png')" alt="Mahai" class="logo-img" />
      </div>

      <h2 class="mahai-auth-title">Giriş</h2>
      <p class="mahai-auth-subtitle">Mahkemeler Yapay Zeka Platformu</p>

      <form @submit.prevent="signIn" class="mahai-auth-form">
        <div class="form-group">
          <label>Kullanıcı Adı</label>
          <div class="input-wrapper">
            <i class="material-symbols-outlined input-icon">person</i>
            <input
              v-model="username"
              type="text"
              placeholder="Kullanıcı Adınızı Giriniz"
              :disabled="loading"
              autocomplete="username"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Şifre</label>
          <div class="input-wrapper">
            <i class="material-symbols-outlined input-icon">lock</i>
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Şifrenizi girin"
              :disabled="loading"
              autocomplete="current-password"
              @keyup.enter="signIn"
            />
            <button
              type="button"
              class="toggle-pw"
              @click="showPassword = !showPassword"
              tabindex="-1"
            >
              <i class="material-symbols-outlined">
                {{ showPassword ? 'visibility_off' : 'visibility' }}
              </i>
            </button>
          </div>
        </div>

        <button type="submit" class="mahai-btn-primary" :disabled="isDisabled">
          <span v-if="!loading && cooldown === 0">Giriş Yap</span>
          <span v-else-if="loading">Giriş yapılıyor…</span>
          <span v-else>Bekleyin…</span>
        </button>

        <div v-if="message" class="mahai-alert success">
          {{ message }}
        </div>
        <div v-if="error" class="mahai-alert error">
          {{ error }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import "@/assets/scss/alerts.css";
import echo from '@/plugins/echo'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  name: 'SignIn',
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      loading: false,
      error: '',
      message: '',
      cooldown: 0,
      cooldownTimer: null,
      stats: null
    }
  },
  computed: {
    isDisabled() {
      return this.loading || this.cooldown > 0
    }
  },
  methods: {
    startCooldown(seconds = 30) {
      this.cooldown = seconds
      if (this.cooldownTimer) clearInterval(this.cooldownTimer)
      this.cooldownTimer = setInterval(() => {
        this.cooldown--
        if (this.cooldown <= 0) {
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
        }
      }, 1000)
    },

    async fetchStats() {
      try {
        const res = await axios.get(`${API_BASE_URL}/public/stats`)
        this.stats = res.data
      } catch {
        // chip'ler sessizce gizli kalır
      }
    },

    async signIn() {
      this.error = ''
      this.message = ''

      if (!this.username || !this.password) {
        this.error = 'Kullanıcı adı ve şifre zorunludur.'
        return
      }
      if (this.isDisabled) return

      this.loading = true

      try {
        const res = await axios.post(
          `${API_BASE_URL}/login`,
          {
            username: this.username,
            password: this.password
          }
        )

        const { user, token } = res.data || {}

        if (token) {
          localStorage.setItem('auth_token', token)
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

          if (echo && echo.connector && echo.connector.options) {
            const conn = echo.connector
            conn.options.auth = conn.options.auth || {}
            conn.options.auth.headers = conn.options.auth.headers || {}
            conn.options.auth.headers['Authorization'] = `Bearer ${token}`
          }

          window.dispatchEvent(new Event('auth-changed'))
        }

        if (user) {
          localStorage.setItem('auth_user', JSON.stringify(user))
        }

        this.message = 'Giriş başarılı. Yönlendiriliyorsunuz…'

        let redirect = this.$route?.query?.redirect

        if (typeof redirect === 'string' && redirect.length > 0) {
          redirect = decodeURIComponent(redirect)
        } else {
          redirect = '/apps/decisions-ai'
        }

        try {
          await this.$router.replace(redirect)
        } catch (navErr) {
          window.location.href = redirect
        }
      } catch (e) {
        const status = e?.response?.status
        const msg = e?.response?.data?.message || e?.message || ''

        if (status === 401) {
          this.error = msg || 'Kullanıcı adı veya şifre hatalı.'
        } else if (status === 403) {
          this.error = msg || 'E-postanızı doğrulamanız gerekiyor.'
        } else if (status === 429 || (msg && msg.toLowerCase().includes('rate'))) {
          this.error = 'Çok sık deneme yapıldı. Lütfen biraz sonra tekrar deneyin.'
          this.startCooldown(60)
        } else {
          this.error = msg || 'Giriş sırasında bir hata oluştu.'
        }
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    this.fetchStats()

    const token = localStorage.getItem('auth_token')
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    }

    if (this.$route?.query?.reason === 'session_expired') {
      this.error = 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.'
    }
  },
  beforeUnmount() {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer)
  }
}
</script>

<style lang="scss" scoped>
.mahai-auth {
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.bg-courthouse {
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  z-index: 0;
}

.bg-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.60);
  z-index: 1;
}

.mahai-auth-card {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 440px;
  background: rgba(17, 24, 39, 0.80);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 16px;
  padding: 48px 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.mahai-auth-logo {
  text-align: center;
  margin-bottom: 32px;

  .logo-img {
    height: 128px;
    width: auto;
    object-fit: contain;
  }
}

.mahai-auth-title {
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px;
}

.mahai-auth-subtitle {
  text-align: center;
  font-size: 15px;
  color: #d1d5db;
  margin: 0 0 32px;
}

.mahai-auth-form {
  .form-group {
    margin-bottom: 20px;

    label {
      display: block;
      font-size: 14px;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 8px;
    }
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.07);
    border: 1.5px solid rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    padding: 0 14px;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus-within {
      border-color: #1a5f4a;
      box-shadow: 0 0 0 3px rgba(26, 95, 74, 0.25);
      background: rgba(255, 255, 255, 0.10);
    }

    .input-icon {
      font-size: 20px;
      color: #9ca3af;
      margin-right: 10px;
      flex-shrink: 0;
    }

    input {
      flex: 1;
      border: none;
      background: transparent;
      padding: 13px 0;
      font-size: 15px;
      color: #ffffff;
      outline: none;

      &::placeholder {
        color: #6b7280;
      }

      &:disabled {
        opacity: 0.6;
      }
    }

    .toggle-pw {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      color: #9ca3af;
      display: flex;
      align-items: center;
      transition: color 0.2s;

      &:hover {
        color: #d1d5db;
      }

      i {
        font-size: 20px;
      }
    }
  }

  .mahai-btn-primary {
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #1a5f4a, #0e3a2d);
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    margin-top: 8px;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 16px rgba(14, 58, 45, 0.5);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .mahai-alert {
    margin-top: 16px;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    text-align: center;

    &.success {
      background: rgba(240, 253, 244, 0.1);
      color: #86efac;
      border: 1px solid rgba(187, 247, 208, 0.3);
    }

    &.error {
      background: rgba(254, 242, 242, 0.1);
      color: #fca5a5;
      border: 1px solid rgba(254, 202, 202, 0.3);
    }
  }
}

.stats-chips {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

.stat-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 99px;
  padding: 6px 14px 6px 10px;
}

.stat-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  animation: chip-pulse 2.4s ease-in-out infinite;

  &.green {
    background: #22c55e;
    box-shadow: 0 0 5px rgba(34, 197, 94, 0.7);
  }

  &.blue {
    background: #c9a227;
    box-shadow: 0 0 5px rgba(201, 162, 39, 0.7);
    animation-delay: 1.2s;
  }
}

@keyframes chip-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.stat-text {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.stat-num {
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
}

.stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1;
}

@media only screen and (max-width: 480px) {
  .mahai-auth-card {
    padding: 32px 24px;
  }

  .mahai-auth-logo .logo-img {
    height: 96px;
  }

  .mahai-auth-title {
    font-size: 20px;
  }
}
</style>
